import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import { Aviso } from "@/shared/ui/Aviso/Aviso";
import { Cargando } from "@/shared/ui/Estados/Cargando";
import { SinDatos } from "@/shared/ui/Estados/SinDatos";
import { Paginacion } from "@/shared/ui/Paginacion/Paginacion";
import { Pastilla } from "@/shared/ui/Pastilla/Pastilla";
import { Tarjeta } from "@/shared/ui/Tarjeta/Tarjeta";
import { fecha, fechaHora, pesos } from "@/shared/utilidades/formato";

// La misma hoja de estilos que Cotizaciones: son la misma clase de lista
// —documentos de World Office con su situación— y deben leerse igual.
import estilos from "@/modules/cotizaciones/PaginaCotizaciones.module.css";

import {
  FILTROS_INICIALES,
  useFacturasDeVenta,
  useRenglonesDeFactura,
  useResumenFacturas,
  type FiltrosFacturas,
} from "./useFacturas";

/** Lo que tiene dentro una factura, cuando se toca. */
function Renglones({ idAsiento }: { idAsiento: number }) {
  const { data, isLoading } = useRenglonesDeFactura(idAsiento);
  if (isLoading) return <Cargando compacto mensaje="PROCESANDO RENGLONES FACTURA..." />;
  if (!data || data.length === 0)
    return <p style={{ padding: "var(--e-4)" }}>Sin renglones.</p>;

  return (
    <table className={estilos.tabla} style={{ margin: "var(--e-2) 0" }}>
      <tbody>
        {data.map((r) => (
          <tr
            key={r.renglon}
            className={estilos.fila}
            style={{ cursor: "default" }}
          >
            <td className={estilos.apagado} style={{ width: 90 }}>
              {r.cantidad ?? "—"} {r.unidad ?? ""}
            </td>
            <td>{r.producto ?? "—"}</td>
            <td className={`${estilos.derecha} ${estilos.apagado}`}>
              {pesos(r.precio_unitario)}
            </td>
            <td className={`${estilos.derecha} ${estilos.cifra}`}>
              {pesos(r.total_renglon)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Sección Facturas de venta · Comercial y Clientes.
 *
 * Johan: «no tenemos sección aún en la app». Una factura se podía enlazar a
 * una orden desde su ficha, pero no había dónde verlas todas: para saber si
 * una FEV quedó en alguna orden había que ir orden por orden. Aquí está la
 * lista, con la orden a la que quedó cada una.
 */
export function PaginaFacturas() {
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [pagina, setPagina] = useState(0);
  const [abierta, setAbierta] = useState<number | null>(null);

  const { facturas, total, porPagina, cargando, error } = useFacturasDeVenta(
    filtros,
    pagina
  );
  const resumen = useResumenFacturas();

  function filtrar(cambio: (p: FiltrosFacturas) => FiltrosFacturas) {
    setFiltros(cambio);
    setPagina(0);
    setAbierta(null);
  }

  // SIN NINGUNA TODAVÍA no es «ningún resultado»: es que el worker de World
  // Office aún no las ha traído. Decirlo evita que alguien crea que se
  // borraron o que la pantalla está rota.
  const nuncaSincronizado = resumen !== undefined && resumen.total === 0;

  return (
    <>
      {resumen && resumen.total > 0 && (
        <div className={estilos.resumen}>
          <div className={estilos.dato}>
            <p className={estilos.valor}>
              {resumen.total.toLocaleString("es-CO")}
            </p>
            <p className={estilos.rotulo}>En World Office</p>
          </div>
          <div className={estilos.dato}>
            <p className={estilos.valor}>
              {resumen.enlazadas.toLocaleString("es-CO")}
            </p>
            <p className={estilos.rotulo}>En una orden</p>
          </div>
          <div className={estilos.dato}>
            <p className={estilos.valor}>
              {(resumen.total - resumen.enlazadas).toLocaleString("es-CO")}
            </p>
            <p className={estilos.rotulo}>Sin enlazar</p>
          </div>
          <div className={estilos.dato}>
            <p className={estilos.valor}>
              {resumen.ultima ? fechaHora(resumen.ultima).split(", ")[1] : "—"}
            </p>
            <p className={estilos.rotulo}>Última sincronización</p>
          </div>
        </div>
      )}

      <Tarjeta
        titulo="Facturas de venta de World Office"
        subtitulo="Toque una para ver lo que se facturó."
        sinRelleno
        pie={
          <Paginacion
            pagina={pagina}
            porPagina={porPagina}
            total={total}
            onCambiar={setPagina}
          />
        }
        acciones={
          <div className={estilos.filtros}>
            <input
              className={estilos.buscador}
              type="search"
              placeholder="Número, cliente o NIT…"
              value={filtros.busqueda}
              onChange={(e) =>
                filtrar((p) => ({ ...p, busqueda: e.target.value }))
              }
              aria-label="Buscar factura"
            />
            <select
              className={estilos.selector}
              value={filtros.dias}
              onChange={(e) =>
                filtrar((p) => ({ ...p, dias: Number(e.target.value) }))
              }
              aria-label="Periodo"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={0}>Todo</option>
            </select>
            <label className={estilos.casilla}>
              <input
                type="checkbox"
                checked={filtros.soloSinEnlazar}
                onChange={(e) =>
                  filtrar((p) => ({ ...p, soloSinEnlazar: e.target.checked }))
                }
              />
              Solo sin enlazar
            </label>
          </div>
        }
      >
        {cargando ? (
          <Cargando mensaje="PROCESANDO FACTURAS VENTA..." />
        ) : error ? (
          <div style={{ padding: "var(--e-5)" }}>
            <Aviso tipo="error" titulo="No se pudieron cargar">
              {error}
            </Aviso>
          </div>
        ) : nuncaSincronizado ? (
          <div style={{ padding: "var(--e-5)" }}>
            <Aviso
              tipo="info"
              titulo="Todavía no ha llegado ninguna factura de World Office"
            >
              La sección está lista, pero el proceso que trae las facturas de
              venta desde World Office aún no se ha puesto en marcha. En cuanto
              empiece, aparecerán aquí solas, con la orden a la que quedó
              enlazada cada una.
            </Aviso>
          </div>
        ) : facturas.length === 0 ? (
          <SinDatos
            titulo="Ninguna factura con esos filtros"
            mensaje="Pruebe ampliando el periodo o quitando la búsqueda."
          />
        ) : (
          <div className={estilos.desplazable}>
            <table className={estilos.tabla}>
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Factura</th>
                  <th style={{ width: 150 }}>Fecha</th>
                  <th>Cliente</th>
                  <th>Vendedor</th>
                  <th className={estilos.derecha} style={{ width: 140 }}>
                    Total
                  </th>
                  <th style={{ width: 150 }}>Orden</th>
                </tr>
              </thead>
              <tbody>
                {facturas.map((f) => (
                  <Fragment key={f.id_asiento}>
                    {/* LAS MISMAS CLASES QUE COTIZACIONES. Con celdas sin clase la
                        tabla salía apeñuscada: el número pegado al borde y la
                        fecha partida en dos líneas. Son la misma clase de lista
                        y tienen que leerse igual. */}
                    <tr
                      className={`${estilos.fila} ${
                        abierta === f.id_asiento ? estilos.filaAbierta : ""
                      }`}
                      onClick={() =>
                        setAbierta((a) =>
                          a === f.id_asiento ? null : f.id_asiento
                        )
                      }
                      tabIndex={0}
                      role="button"
                      aria-expanded={abierta === f.id_asiento}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setAbierta((a) =>
                            a === f.id_asiento ? null : f.id_asiento
                          );
                        }
                      }}
                      style={f.anulado ? { opacity: 0.55 } : undefined}
                    >
                      <td className={estilos.numero}>{f.documento}</td>
                      <td className={estilos.apagado}>
                        {f.fecha ? fecha(f.fecha) : "—"}
                      </td>
                      <td>{f.cliente_nombre ?? "—"}</td>
                      <td className={estilos.apagado}>
                        {f.vendedor_nombre ?? "—"}
                      </td>
                      <td className={`${estilos.derecha} ${estilos.cifra}`}>
                        {pesos(f.total)}
                      </td>
                      <td>
                        {f.anulado ? (
                          <Pastilla tono="detenido">Anulada</Pastilla>
                        ) : f.orden_servicio_id ? (
                          <Link
                            to={`/ordenes/${f.orden_servicio_id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Pastilla tono="marca">
                              {f.orden_numero ?? "En una orden"}
                            </Pastilla>
                          </Link>
                        ) : (
                          <Pastilla tono="neutro">Sin enlazar</Pastilla>
                        )}
                      </td>
                    </tr>
                    {abierta === f.id_asiento && (
                      <tr className={estilos.filaDetalle}>
                        <td colSpan={6}>
                          <Renglones idAsiento={f.id_asiento} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>
    </>
  );
}
